import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiService } from "@/api/api";
import { useToast } from "@/hooks/use-toast";
import SOAPTemplate from "@/components/soap/SOAPTemplate";
import MultiSearchInput from "@/components/diagnosis/MultiSearchInput";
import { Button } from "@/components/ui/button";
import { CheckCircle, Copy, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

// Import diagnosis codes data
import diagnosisCodesData from "@/data/diagnosisCodes.json";

interface DiagnosisCode {
  code: string;
  description: string;
}

interface NavigationState {
  sessionTitle?: string;
  finalNote?: string;
  fromConsultation?: boolean;
  processingDuration?: number;
}

const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL = 1000;

const NoteRefinementComponent = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [loadingNote, setLoadingNote] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [templateData, setTemplateData] = useState<any>({});
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [sessionTitle, setSessionTitle] = useState("Consultation Note");
  const [diagnosisSuggestions, setDiagnosisSuggestions] = useState<string[]>([]);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedFromStateRef = useRef(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Parse final_note markdown into SOAP sections
  const parseFinalNote = (finalNote: string): Record<string, string> => {
    const sections: Record<string, string> = {
      subjective: '',
      objective: '',
      assessment: '',
      plan: ''
    };

    // Split by section headers like **SUBJECTIVE**, **OBJECTIVE**, etc.
    const subjectiveMatch = finalNote.match(/\*\*SUBJECTIVE\*\*\s*([\s\S]*?)(?=\*\*OBJECTIVE\*\*|$)/i);
    const objectiveMatch = finalNote.match(/\*\*OBJECTIVE\*\*\s*([\s\S]*?)(?=\*\*ASSESSMENT\*\*|$)/i);
    const assessmentMatch = finalNote.match(/\*\*ASSESSMENT\*\*\s*([\s\S]*?)(?=\*\*PLAN\*\*|$)/i);
    const planMatch = finalNote.match(/\*\*PLAN\*\*\s*([\s\S]*?)$/i);

    if (subjectiveMatch) sections.subjective = subjectiveMatch[1].trim();
    if (objectiveMatch) sections.objective = objectiveMatch[1].trim();
    if (assessmentMatch) sections.assessment = assessmentMatch[1].trim();
    if (planMatch) sections.plan = planMatch[1].trim();

    return sections;
  };

  // Process session data from API response
  const processSessionData = useCallback((sessionPayload: any): boolean => {
    // First check for final_note (from Finisher Lambda)
    const finalNote = sessionPayload.final_note;
    if (finalNote && typeof finalNote === 'string' && finalNote.trim()) {
      console.log("[NoteRefinement] Using final_note from Finisher Lambda");
      const parsedSections = parseFinalNote(finalNote);
      setTemplateData(parsedSections);
      setSessionTitle(sessionPayload.session_title || "Consultation Note");

      let codesToSet: string[] = [];
      if (sessionPayload.diagnosis && typeof sessionPayload.diagnosis === 'string') {
        const trimmedDiagnosis = sessionPayload.diagnosis.trim();
        if (trimmedDiagnosis) {
          codesToSet = trimmedDiagnosis.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
      }
      setSelectedCodes(codesToSet);
      return true;
    }

    // Fallback to enhanced_sections (legacy or manual updates)
    const rawSections = sessionPayload.enhanced_sections || {};

    const hasContent = Object.values(rawSections).some(
      (val: any) => val && (typeof val === 'string' ? val.trim() : val.content?.trim())
    );

    if (!hasContent) {
      return false;
    }

    const flattenedData: Record<string, string> = Object.keys(rawSections).reduce((acc, key) => {
      const sectionValue = (rawSections as any)[key];
      if (typeof sectionValue === 'string') {
        acc[key] = sectionValue;
      } else if (typeof sectionValue === 'object' && sectionValue !== null && 'content' in sectionValue) {
        acc[key] = sectionValue.content;
      } else {
        acc[key] = '';
      }
      return acc;
    }, {} as Record<string, string>);

    setTemplateData(flattenedData);

    let codesToSet: string[] = [];
    if (sessionPayload.diagnosis && typeof sessionPayload.diagnosis === 'string') {
      const trimmedDiagnosis = sessionPayload.diagnosis.trim();
      if (trimmedDiagnosis) {
        codesToSet = trimmedDiagnosis.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
    }
    setSelectedCodes(codesToSet);
    setSessionTitle(sessionPayload.session_title || "Consultation Note");

    return true;
  }, []);

  useEffect(() => {
    if (!sessionId) {
      toast({
        title: "Error",
        description: "No session ID provided.",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    // Load diagnosis suggestions
    const formattedSuggestions = (diagnosisCodesData as DiagnosisCode[]).map(
      (item: DiagnosisCode) => `${item.code} - ${item.description}`
    );
    setDiagnosisSuggestions(formattedSuggestions);

    // Check navigation state first (instant load from consultation flow)
    const navState = location.state as NavigationState | null;
    const fromConsultation = navState?.fromConsultation === true;

    if (fromConsultation) {
      console.log("[NoteRefinement] Coming from consultation flow - will fetch with confidence");
      if (navState?.sessionTitle) {
        setSessionTitle(navState.sessionTitle);
      }
    }

    // Fetch session data
    const fetchSessionNote = async (attemptNumber: number = 0): Promise<boolean> => {
      try {
        const sessionApiResponse = await apiService.getSessionById(sessionId);
        console.log(`[NoteRefinement] Fetch attempt ${attemptNumber}:`, sessionApiResponse);

        const sessionPayload = sessionApiResponse.data;

        if (!sessionPayload) {
          if (attemptNumber === 0 && !fromConsultation) {
            toast({
              title: "Error",
              description: "Session data not found.",
              variant: "destructive",
            });
            navigate("/");
          }
          return false;
        }

        // Check if still processing
        const isStillProcessing =
          sessionPayload.session_status === 'processing' ||
          sessionPayload.processingStatus === 'PROCESSING';

        if (isStillProcessing && !fromConsultation) {
          // If not from consultation and still processing, don't poll
          console.log("[NoteRefinement] Note still processing");
          return false;
        }

        // Try to process the data
        const success = processSessionData(sessionPayload);

        if (!success && fromConsultation) {
          // Coming from consultation but data not ready yet - keep polling
          console.log("[NoteRefinement] Data not ready yet, will poll");
          return false;
        }

        return success;

      } catch (error) {
        console.error("[NoteRefinement] Fetch error:", error);
        if (attemptNumber === 0 && !fromConsultation) {
          toast({
            title: "Error",
            description: "Failed to load consultation note.",
            variant: "destructive",
          });
          navigate("/");
        }
        return false;
      }
    };

    // Main initialization
    const init = async () => {
      setLoadingNote(true);

      // First attempt
      const success = await fetchSessionNote(0);

      if (success) {
        setLoadingNote(false);
        return;
      }

      // If from consultation flow, start polling (we know data will be ready soon)
      if (fromConsultation) {
        console.log("[NoteRefinement] Starting poll - from consultation flow");
        setIsPolling(true);
        setPollAttempts(0);

        pollIntervalRef.current = setInterval(async () => {
          setPollAttempts(prev => {
            const newAttempts = prev + 1;

            if (newAttempts >= MAX_POLL_ATTEMPTS) {
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
              setIsPolling(false);
              setLoadingNote(false);
              toast({
                title: "Processing Taking Longer",
                description: "Please refresh the page in a moment.",
                variant: "default",
              });
              return newAttempts;
            }

            fetchSessionNote(newAttempts).then(success => {
              if (success) {
                if (pollIntervalRef.current) {
                  clearInterval(pollIntervalRef.current);
                }
                setIsPolling(false);
                setLoadingNote(false);
              }
            });

            return newAttempts;
          });
        }, POLL_INTERVAL);
      } else {
        // Not from consultation and first fetch failed - show error state
        setLoadingNote(false);
      }
    };

    init();

  }, [sessionId, navigate, toast, location.state, processSessionData]);

  const handleTemplateDataChange = useCallback((updatedData: any) => {
    setTemplateData((prevData: any) => ({ ...prevData, ...updatedData }));
  }, []);

  const handleCodesChange = useCallback((codes: string[]) => {
    setSelectedCodes(codes);
  }, []);

  const handleSaveNote = async () => {
    if (!sessionId) {
      toast({
        title: "Save Error",
        description: "Session ID missing.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        enhanced_sections: {
          subjective: templateData.subjective || '',
          objective: templateData.objective || '',
          assessment: templateData.assessment || '',
          plan: templateData.plan || '',
          diagnosis: selectedCodes.join(', '),
        },
        session_title: sessionTitle,
        session_status: 'completed'
      };

      await apiService.updateSessionData(sessionId, payload);

      toast({
        title: "Note Saved!",
        description: "The session note has been updated successfully.",
        variant: "success",
      });

      navigate(`/session/${sessionId}`);

    } catch (error) {
      console.error("Failed to update session note:", error);
      toast({
        title: "Update Failed",
        description: "There was an error saving your note.",
        variant: "destructive",
      });
    }
  };

  const handleCopyNote = useCallback(() => {
    const allContent = [
      templateData.subjective && `Subjective: ${templateData.subjective}`,
      templateData.objective && `Objective: ${templateData.objective}`,
      templateData.assessment && `Assessment: ${templateData.assessment}`,
      templateData.plan && `Plan: ${templateData.plan}`,
      selectedCodes.length > 0 ? `Diagnosis Codes: ${selectedCodes.join(', ')}` : ''
    ].filter(Boolean).join('\n\n');

    if (allContent.trim()) {
      navigator.clipboard.writeText(allContent).then(
        () => toast({ title: "Copied!", description: "Note copied to clipboard." }),
        () => toast({ title: "Copy Failed", variant: "destructive" })
      );
    } else {
      toast({ title: "No Content", description: "Nothing to copy.", variant: "destructive" });
    }
  }, [templateData, selectedCodes, toast]);

  if (loadingNote) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm p-6">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
        <p className="mt-4 text-lg text-gray-700">Loading your note...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 w-full h-full flex flex-col overflow-hidden relative">
      <h2 className="text-2xl font-bold mb-4">{sessionTitle} - Note Refinement</h2>
      <div className="h-px bg-gray-200 mb-6 w-full"></div>
      <div className="flex-1 overflow-y-auto mb-4">
        <SOAPTemplate
          data={templateData}
          onDataChange={handleTemplateDataChange}
          dictationPhase="refine"
          className="space-y-4"
        />

        <MultiSearchInput
          selectedItems={selectedCodes}
          onChange={handleCodesChange}
          searchSuggestions={diagnosisSuggestions}
          placeholder="Search and add diagnosis codes..."
        />
      </div>

      <div className="bg-white border-t border-gray-200 pt-4 mt-auto flex-shrink-0">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-center gap-4 h-[108px]">
          <Button onClick={handleCopyNote} variant="outline" size="lg" className="h-12">
            <Copy className="mr-2 h-4 w-4" />Copy note
          </Button>
          <Button onClick={handleSaveNote} size="lg" className="bg-green-600 hover:bg-green-700 h-12">
            <CheckCircle className="mr-2 h-4 w-4" />Approve & Save Note
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoteRefinementComponent;
